import { getPublicIp } from '../../utils/publicIp';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import api from '../../api';
import '../../assets/css/topNav.css';
import { useUser } from '../../utils/hooks/user';
import { useDispatch } from 'react-redux';
import { useRootMemoSelector } from 'use-redux-states';
import { logoutUser } from '../../store/auth';
import Broadcasts, { CreateBroadcast } from '../Broadcasts';
import LanguageSelector from './LanguageSelector';
import OnboardingMenu from './OnboardingMenu';
import FeatureGate from './FeatureGate';

import {
  GiftOutlined,
  LogoutOutlined,
  DownOutlined,
  UserOutlined
} from '@ant-design/icons';
import { message, Layout, Dropdown, Avatar } from 'antd';
import Language from '../../utils/Language';

const { Header: AntHeader } = Layout;
const source_url = window.location.href;

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [ip, setIp] = useState('');

  const companyData = useRootMemoSelector('app.company');
  const { id: agentId, profile_picture, meta } = useUser();
  const defaultLanguage = meta?.language || companyData?.options?.language || 'en';
  const agentLanguageKey = agentId ? `sawport-agent-language:${agentId}` : null;
  const [languageOverride, setLanguageOverride] = useState<{ agentId: string; language: string } | null>(null);
  const agentLanguage =
    (languageOverride?.agentId === agentId && languageOverride?.language) ||
    (agentLanguageKey && sessionStorage.getItem(agentLanguageKey)) ||
    defaultLanguage;

  const handleLanguageChange = (language: string) => {
    if (agentLanguageKey) sessionStorage.setItem(agentLanguageKey, language);
    setLanguageOverride({ agentId: agentId || '', language });
  };

  const handleLogout = async () => {
    try {
      await api.post('/logout', { source_url, ip });
    } catch (error) {
      message.error('Something went wrong', 5);
    }
    dispatch(logoutUser());
    navigate('/auth/login');
  };

  useEffect(() => {
    // Check if IP is already fetched
    if (!ip) {
      getPublicIp()
        .then((data) => {
          const { ip: fetchedIp } = data.data;
          setIp(fetchedIp);
        })
        .catch((error) => console.log('error => ', error));
    }
  }, []);

  useEffect(() => {
    if (!companyData?.id) return;

    const applyAgentLanguage = () => {
      if (!document.hidden) Language.setLanguage(agentLanguage);
    };
    applyAgentLanguage();
    document.addEventListener('visibilitychange', applyAgentLanguage);
    return () => document.removeEventListener('visibilitychange', applyAgentLanguage);
  }, [companyData?.id, agentLanguage]);

  return (
    <AntHeader className='fixed py-6 w-full h-16 z-50 bg-white shadow-sm'>
      <div className='fixed top-0 right-0 h-16 px-6 flex items-center !gap-4'>
        <LanguageSelector className='hidden md:flex' value={agentLanguage} onChange={handleLanguageChange} />
        <span
          title='Help Center'
          className='hidden md:inline-flex cursor-pointer'
          data-tour='header-help'>
          <OnboardingMenu />
        </span>
        <FeatureGate feature='broadcast'>
          <span title='Broadcast' className='hidden md:inline-flex cursor-pointer'>
            <CreateBroadcast />
          </span>
          <span
            title='Notifications'
            className='hidden md:inline-flex cursor-pointer'
            data-tour='header-notifications'>
            <Broadcasts />
          </span>
        </FeatureGate>
        <span title='Updates' className='hidden md:inline-flex cursor-pointer'>
          <GiftOutlined
            style={{ fontSize: '18px' }}
            onClick={() =>
              window.open(
                'https://sawport-team-d19g630i.atlassian.net/servicedesk/customer/portals'
              )
            }
            id='updates'
          />
        </span>
        <div className='profileIcon' data-tour='header-profile'>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'language-mobile',
                  icon: <LanguageSelector className='md:hidden w-full' value={agentLanguage} onChange={handleLanguageChange} />,
                  label: '',
                  className: 'md:hidden p-0'
                },
                {
                  key: 'profile',
                  icon: <UserOutlined />,
                  label: 'Profile',
                  onClick: () => navigate('/profile')
                },
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: 'Logout',
                  onClick: handleLogout
                }
              ]
            }}
            trigger={['click']}>
            <a onClick={(e) => e.preventDefault()} className='flex items-center cursor-pointer'>
              <Avatar
                src={profile_picture}
                size='default'
                style={{ marginRight: '8px', border: '1.5px solid #083047' }}
              />
              <DownOutlined />
            </a>
          </Dropdown>
        </div>
      </div>
    </AntHeader>
  );
};

export default Header;
